/**
 * The canonical list of things a church would call "a feature".
 *
 * WHY IT EXISTS. Three places already faked this list and none of them was usable as one: `CAPABILITIES`
 * (../feature-flags) knows which flag gates what but has no display metadata; `church-nav.ts` is rich but
 * nav-shaped and duplicated per context; and `included-features-card.tsx` hand-maintains an
 * ALWAYS_INCLUDED / FLAGGED pair that has to be edited whenever the product changes. An operator choosing
 * which feature to mark "New" needs the union of those, so it is written down once here.
 *
 * IT GATES NOTHING. This file decides no access and no rollout — it is a directory. `capability` is recorded
 * so a screen can find a feature's flag, never so this file can enforce it.
 *
 * KEYS ARE CODE, METADATA IS DATA. The admin dashboard can change how a feature is presented; it cannot
 * invent a feature. A row in `platform_features` whose key is not here is ignored at delivery, so deleting an
 * entry can never break the tenant — the badge simply stops appearing.
 */

import type { Capability } from "../feature-flags";

/** Stable identifiers. These are persisted in `platform_features.key` — renaming one orphans its row. */
export enum ProductFeature {
  Overview = "overview",
  Members = "members",
  Groups = "groups",
  Ministries = "ministries",
  Activities = "activities",
  Departments = "departments",
  Finance = "finance",
  Pledges = "pledges",
  Budgets = "budgets",
  Reports = "reports",
  Assets = "assets",
  Forms = "forms",
  WorkQueue = "work-queue",
  Branches = "branches",
  GlobalFinance = "global-finance",
  AuditLog = "audit-log",
  MyBranch = "my-branch",
  Settings = "settings",
}

export type ProductFeatureDefinition = {
  key: ProductFeature;
  /** i18n key for the name shown in the ADMIN list, under `admin.platformFeatures.features.*`. */
  nameKey: string;
  /**
   * The tenant routes this feature owns.
   *
   * A list, not a string: the same feature is reached by different paths in the branch and parish navs —
   * Ministries is `/members/ministries` inside a branch and `/ministries` from the parish. A badge has to
   * find its row in both.
   */
  navUrls: readonly string[];
  /** The flag capability gating it, when it has one. Recorded for lookup; never enforced here. */
  capability: Capability | null;
};

const define = (
  key: ProductFeature,
  navUrls: readonly string[],
  capability: Capability | null = null,
): ProductFeatureDefinition => ({
  key,
  nameKey: `features.${key}`,
  navUrls,
  capability,
});

export const PRODUCT_FEATURES: Record<ProductFeature, ProductFeatureDefinition> = {
  [ProductFeature.Overview]: define(ProductFeature.Overview, ["/"]),
  [ProductFeature.Members]: define(ProductFeature.Members, ["/members"]),
  [ProductFeature.Groups]: define(ProductFeature.Groups, ["/members/groups"]),
  [ProductFeature.Ministries]: define(ProductFeature.Ministries, ["/members/ministries", "/ministries"]),
  [ProductFeature.Activities]: define(ProductFeature.Activities, ["/activities"]),
  [ProductFeature.Departments]: define(ProductFeature.Departments, ["/departments"]),
  [ProductFeature.Finance]: define(ProductFeature.Finance, ["/finance"]),
  [ProductFeature.Pledges]: define(ProductFeature.Pledges, ["/finance/pledges"], "pledges.view"),
  [ProductFeature.Budgets]: define(ProductFeature.Budgets, ["/budgets"], "budgeting.view"),
  [ProductFeature.Reports]: define(ProductFeature.Reports, ["/reports"], "reports.view"),
  [ProductFeature.Assets]: define(ProductFeature.Assets, ["/assets"], "assets.view"),
  [ProductFeature.Forms]: define(ProductFeature.Forms, ["/forms"], "forms.view"),
  [ProductFeature.WorkQueue]: define(ProductFeature.WorkQueue, ["/work-queue"], "workQueue.view"),
  [ProductFeature.Branches]: define(ProductFeature.Branches, ["/branches"]),
  [ProductFeature.GlobalFinance]: define(ProductFeature.GlobalFinance, ["/global-finance"]),
  [ProductFeature.AuditLog]: define(ProductFeature.AuditLog, ["/audit-log"]),
  [ProductFeature.MyBranch]: define(ProductFeature.MyBranch, ["/my-branch"]),
  [ProductFeature.Settings]: define(ProductFeature.Settings, ["/settings"]),
};

/** Every feature, in a stable order — what the admin list renders. */
export const PRODUCT_FEATURE_LIST: readonly ProductFeatureDefinition[] = Object.values(PRODUCT_FEATURES);

/** True when a persisted key still corresponds to a real feature. Rows failing this are ignored, not deleted. */
export const isKnownProductFeature = (key: string): key is ProductFeature =>
  Object.prototype.hasOwnProperty.call(PRODUCT_FEATURES, key);

/**
 * The feature that owns a nav route EXACTLY, or null. Used to badge a nav row, which is an exact URL.
 *
 * Longest match wins: `/finance/pledges` is Pledges, not Finance, and the order entries happen to be
 * declared in must not decide that.
 */
export const findProductFeatureForNavUrl = (url: string): ProductFeatureDefinition | null => {
  let best: ProductFeatureDefinition | null = null;
  let bestLength = -1;
  for (const definition of PRODUCT_FEATURE_LIST) {
    for (const navUrl of definition.navUrls) {
      if (navUrl === url && navUrl.length > bestLength) {
        best = definition;
        bestLength = navUrl.length;
      }
    }
  }
  return best;
};

/**
 * The feature a user is CURRENTLY INSIDE, for any route beneath it.
 *
 * Different question from the exact match above: `/members/list/abc123` is still Members, and a tutorial
 * offered on a feature should stay reachable on that feature's sub-pages rather than blinking out the moment
 * somebody opens a record. Longest prefix wins, so `/members/groups` resolves to Groups and not Members.
 *
 * The boundary check matters — without it `/members-archive` would match `/members`, which is a different
 * feature that happens to share a prefix.
 */
export const findProductFeatureForPath = (path: string): ProductFeatureDefinition | null => {
  const normalized = path.length > 1 && path.endsWith("/") ? path.slice(0, -1) : path;
  let best: ProductFeatureDefinition | null = null;
  let bestLength = -1;

  for (const definition of PRODUCT_FEATURE_LIST) {
    for (const navUrl of definition.navUrls) {
      // "/" owns only itself; treating it as a prefix would make Overview match every route in the app.
      const matches =
        navUrl === "/"
          ? normalized === "/"
          : normalized === navUrl || normalized.startsWith(`${navUrl}/`);
      if (matches && navUrl.length > bestLength) {
        best = definition;
        bestLength = navUrl.length;
      }
    }
  }
  return best;
};
